// src/components/ecommerce/SearchModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  X,
  Search,
  Clock,
  TrendingUp,
  Filter,
  Star,
  Package,
  Tag,
  DollarSign,
  Grid,
  List,
  SlidersHorizontal
} from 'lucide-react';

export const SearchModal = ({ isOpen, onClose, products = [], onProductSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [popularSearches] = useState([
    'Electronics', 'Clothing', 'Shoes', 'Accessories', 'Home', 'Sports'
  ]);
  const [filters, setFilters] = useState({
    category: '',
    priceRange: '',
    rating: '',
    inStock: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Load recent searches from localStorage
      const saved = JSON.parse(localStorage.getItem('ecommerce_recent_searches') || '[]');
      setRecentSearches(saved);
      
      // Focus search input
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim()) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, filters]);

  const performSearch = () => {
    let results = products.filter(product => {
      const matchesQuery = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          product.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          product.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
                          product.category?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = !filters.category || product.category === filters.category;
      
      const matchesPrice = !filters.priceRange || (() => {
        const price = product.salePrice || product.price || 0;
        switch (filters.priceRange) {
          case 'under-100': return price < 100;
          case '100-500': return price >= 100 && price <= 500;
          case '500-1000': return price >= 500 && price <= 1000;
          case 'over-1000': return price > 1000;
          default: return true;
        }
      })();

      const matchesRating = !filters.rating || (product.rating >= parseInt(filters.rating));
      
      const matchesStock = !filters.inStock || (product.quantity > 0);

      return matchesQuery && matchesCategory && matchesPrice && matchesRating && matchesStock;
    });

    // Sort by relevance (name matches first, then description, then tags)
    results.sort((a, b) => {
      const aNameMatch = a.name.toLowerCase().includes(searchQuery.toLowerCase());
      const bNameMatch = b.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      
      return 0;
    });

    setSearchResults(results);
  };

  const saveSearch = (query) => {
    if (!query.trim()) return;
    
    const searches = JSON.parse(localStorage.getItem('ecommerce_recent_searches') || '[]');
    const newSearches = [query, ...searches.filter(s => s !== query)].slice(0, 10);
    
    localStorage.setItem('ecommerce_recent_searches', JSON.stringify(newSearches));
    setRecentSearches(newSearches);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    saveSearch(query);
  };

  const handleProductClick = (product) => {
    saveSearch(searchQuery);
    onProductSelect?.(product);
    onClose();
  };

  const clearRecentSearches = () => {
    localStorage.removeItem('ecommerce_recent_searches');
    setRecentSearches([]);
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b border-gray-200">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  handleSearch(searchQuery);
                }
              }}
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-3 rounded-lg border ${showFilters ? 'bg-blue-50 border-blue-300' : 'border-gray-300'}`}
          >
            <SlidersHorizontal className="h-5 w-5" />
          </button>
          
          <button onClick={onClose} className="p-3 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <select
                value={filters.priceRange}
                onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Any Price</option>
                <option value="under-100">Under R100</option>
                <option value="100-500">R100 - R500</option>
                <option value="500-1000">R500 - R1000</option>
                <option value="over-1000">Over R1000</option>
              </select>

              <select
                value={filters.rating}
                onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Any Rating</option>
                <option value="4">4+ Stars</option>
                <option value="3">3+ Stars</option>
                <option value="2">2+ Stars</option>
              </select>

              <label className="flex items-center gap-2 px-3 py-2">
                <input
                  type="checkbox"
                  checked={filters.inStock}
                  onChange={(e) => setFilters(prev => ({ ...prev, inStock: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm">In Stock Only</span>
              </label>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {searchQuery.trim() ? (
            // Search Results
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">
                  {searchResults.length} results for "{searchQuery}"
                </h3>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {searchResults.length > 0 ? (
                <div className={`grid gap-4 ${
                  viewMode === 'grid' 
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' 
                    : 'grid-cols-1'
                }`}>
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      onClick={() => handleProductClick(product)}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md cursor-pointer transition-shadow"
                    >
                      <div className={`flex ${viewMode === 'grid' ? 'flex-col' : 'flex-row'} gap-4`}>
                        <div className={`${viewMode === 'grid' ? 'aspect-square' : 'w-20 h-20'} bg-gray-100 rounded-lg flex-shrink-0`}>
                          {product.images?.[0] || product.imageUrl ? (
                            <img
                              src={product.images?.[0] || product.imageUrl}
                              alt={product.name}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">{product.name}</h4>
                          
                          {product.description && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {product.description}
                            </p>
                          )}

                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-bold text-gray-900">
                              R{((product.salePrice || product.price) || 0).toFixed(2)}
                            </span>
                            {product.salePrice && product.salePrice < product.price && (
                              <span className="text-sm text-gray-500 line-through">
                                R{product.price.toFixed(2)}
                              </span>
                            )}
                          </div>

                          {product.rating && (
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-3 w-3 ${
                                    i < Math.floor(product.rating)
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                              <span className="text-xs text-gray-500 ml-1">
                                ({product.reviewCount || 0})
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                  <p className="text-gray-600">
                    Try adjusting your search terms or filters
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Search Suggestions
            <div className="p-6 space-y-6">
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Recent Searches
                    </h3>
                    <button
                      onClick={clearRecentSearches}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleSearch(search)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                      >
                        {search}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Searches */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Popular Searches
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularSearches.map((search, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearch(search)}
                      className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                    >
                      {search}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Tips */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Search Tips</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Use specific product names for better results</li>
                  <li>• Try searching by category (e.g., "electronics", "clothing")</li>
                  <li>• Use filters to narrow down your search</li>
                  <li>• Search by brand name or product features</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};